import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonPropsDrawer } from '@celebration/react/ClbDrawer/ClbDrawer.types';
import Drawer from '../../../../common/components/drawer';
import Input from '../../../../common/components/input';
import { MASKTYPE } from '../../../../common/components/input/input-constants';
import placeholderPatterns from '../../../../common/components/input/placeholder-patterns';
import getRegionsData from '../../../../common/constants/multi-select-data';
import IBranchesFiltersModel from '../../interfaces/IBranchesFiltersModel';
import IBranchesFilter from './interfaces/IBranchesfilters';
import './branches-filters.scss';
import getBranchStatusData from '../../../../common/constants/multi-select-branch-status';
import RangePicker from '../../../../common/components/range-picker';
import SelectAutocomplete from '../../../../common/components/select-autocomplete';

function BranchesFilter(props: Readonly<IBranchesFilter>) {
    const {
        currentFilters,
        onApplyFilter,
        isShowingFilters,
        toggleShowFilters,
    } = props;
    currentFilters.regions = currentFilters.regions ?? [];
    currentFilters.status = currentFilters.status ?? [];
    const { t } = useTranslation();
    const [filtersModel, setFiltersModel] =
        useState<IBranchesFiltersModel>(currentFilters);

    const onClearFilters = useCallback(() => {
        setFiltersModel({
            status: [],
            regions: [],
            corporateName: '',
            cnpj: '',
            sapCode: '',
            tmsCode: '',
            eccCode: '',
            requestedBy: '',
            startDateApproved: undefined,
            endDateApproved: undefined,
            description: currentFilters.description,
        });
    }, [currentFilters.description, setFiltersModel]);

    const clearFilterButton: ButtonPropsDrawer = {
        key: 'branch-filter-clear-button',
        id: 'branch-filter-clear-button',
        label: t('Limpar'),
        disabled: false,
        loading: false,
        title: t('Limpar'),
        onClick: () => onClearFilters(),
        'data-testid': 'branch-filter-clear-button',
        role: 'button',
        size: 'md',
        styleType: 'secondary',
    };

    const submitFilterButton: ButtonPropsDrawer = {
        key: 'branch-filter-submit-button',
        id: 'branch-filter-submit-button',
        label: t('Filtrar'),
        disabled: false,
        loading: false,
        title: t('FilialFiltrar'),
        onClick: () => onApplyFilter(filtersModel),
        'data-testid': 'branch-filter-submit-button',
        role: 'button',
        size: 'md',
        styleType: 'primary',
    };

    const buttonsList: [ButtonPropsDrawer, ButtonPropsDrawer?] = [
        submitFilterButton,
        clearFilterButton,
    ];

    return (
        <Drawer
            title={t('FilialFiltrar')}
            open={isShowingFilters}
            onClose={toggleShowFilters}
            buttonsFooter={buttonsList}
            data-testid="branches-filters"
        >
            <Input
                label={t('FilialNome')}
                className="branch-corporate-name"
                placeholder={t('RazaoSocial')}
                value={filtersModel.corporateName}
                onChange={(e: any) => {
                    setFiltersModel({
                        ...filtersModel,
                        corporateName: e.target.value,
                    });
                }}
                dataTestId="branches-filters-corporate-name"
            />

            <SelectAutocomplete
                mode="multiple"
                label={t('RegioesAtendidas')}
                value={filtersModel.regions}
                onChange={(e: any) => {
                    setFiltersModel({ ...filtersModel, regions: e });
                }}
                placeholder={t('RegioesAtendidas')}
                dataTestId="branches-filters-regions"
                options={getRegionsData(t)}
                getPopupContainer={node =>
                    (node.parentNode as HTMLElement) ?? document.body
                }
            />

            <Input
                label="CNPJ"
                mask={MASKTYPE.CNPJ}
                value={filtersModel.cnpj}
                placeholder={placeholderPatterns.CNPJ}
                onChange={(e: any) => {
                    setFiltersModel({ ...filtersModel, cnpj: e.target.value });
                }}
                dataTestId="branches-filters-cnpj"
            />

            <Input
                label={t('CodigoSapReduzido')}
                value={filtersModel.sapCode}
                placeholder={placeholderPatterns.SAP_S4}
                maxlength={10}
                onChange={(e: any) => {
                    setFiltersModel({
                        ...filtersModel,
                        sapCode: e.target.value,
                    });
                }}
                dataTestId="branches-filters-sap-code"
            />

            <Input
                label={t('CodigoTmsReduzido')}
                value={filtersModel.tmsCode}
                placeholder={placeholderPatterns.TMS}
                maxlength={10}
                onChange={(e: any) => {
                    setFiltersModel({
                        ...filtersModel,
                        tmsCode: e.target.value,
                    });
                }}
                dataTestId="branches-filters-tms-code"
            />

            <Input
                label={t('CodigoEccReduzido')}
                value={filtersModel.eccCode}
                placeholder={placeholderPatterns.SAP_ECC}
                maxlength={6}
                onChange={(e: any) => {
                    setFiltersModel({
                        ...filtersModel,
                        eccCode: e.target.value,
                    });
                }}
                dataTestId="branches-filters-ecc-code"
            />

            <SelectAutocomplete
                mode="multiple"
                label="Status"
                value={filtersModel.status}
                onChange={(e: any) => {
                    setFiltersModel({ ...filtersModel, status: e });
                }}
                placeholder="Status"
                dataTestId="branches-filters-status"
                options={getBranchStatusData(t)}
                getPopupContainer={node =>
                    (node.parentNode as HTMLElement) ?? document.body
                }
            />

            <Input
                label={t('Solicitante')}
                value={filtersModel.requestedBy}
                placeholder={t('SolicitadoPor')}
                onChange={(e: any) => {
                    setFiltersModel({
                        ...filtersModel,
                        requestedBy: e.target.value,
                    });
                }}
                dataTestId="branches-filters-requestedBy"
            />

            <RangePicker
                title={t('PeriodoAprovacao')}
                startDateLabel={t('PeriodoInicio')}
                startDate={filtersModel.startDateApproved}
                onChangeStartDate={(value: any) => {
                    setFiltersModel({
                        ...filtersModel,
                        startDateApproved: value,
                    });
                }}
                endDateLabel={t('PeriodoFim')}
                endDate={filtersModel.endDateApproved}
                onChangeEndDate={(value: any) => {
                    setFiltersModel({
                        ...filtersModel,
                        endDateApproved: value,
                    });
                }}
                getPopupContainer={node =>
                    (node.parentNode as HTMLElement) ?? document.body
                }
            />
        </Drawer>
    );
}

export default BranchesFilter;
